import { Divider } from "@landtechnologies/components";
import { addMonths } from "date-fns";
import { useEffect, useMemo } from "react";
import { useSkipTracingCreditsQuery } from "@gql/li-api/graphql";

import Feature from "src/js/stores/user/Feature";
import { mastermapTilesToViews } from "src/js/util/units_and_constants";

import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Account } from "react-migration/domains/constraints/typings/applicationTypes/Account";
import {
  LandTechEndpoints,
  routedClient,
} from "react-migration/lib/persistence/apollo/routedClient";

import { UsageRow } from "./UsageRow";
import ACCOUNT_DOCUMENT from "../apollo/queries/account.gql";
import { useAccountQuery } from "../hooks/useAccountQuery";
import { CurrentPlan } from "./CurrentPlan";
import { OwnershipViews } from "./OwnershipViews";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import { formatDate } from "react-migration/domains/comparables/util/Dates";

export const Usage = () => {
  const { t } = useTranslation();
  const { data } = useAccountQuery();
  const account = data?.accountWithViews as Account;

  useEffect(() => {
    routedClient.refetchQueries({ include: [ACCOUNT_DOCUMENT] });
  }, []);

  // work out the ownership date + the start of the billing cycle
  const ownershipViewsResetDate = account?.ownership_views.next_reset_date ?? "";
  let ownershipViewsBillingCycleStart = undefined;
  if (ownershipViewsResetDate !== "") {
    ownershipViewsBillingCycleStart = addMonths(
      new Date(ownershipViewsResetDate),
      -1
    ).toISOString();
  }

  const { data: creditsResponse } = useSkipTracingCreditsQuery({
    client: routedClient,
    fetchPolicy: "network-only",
    context: {
      endpoint: LandTechEndpoints.PrivateClient,
    },
  });

  const accountSkipTraceCredit = useMemo(
    () => ({
      cap: creditsResponse?.accountWithViews.skip_traces?.cap ?? 0,
      used: creditsResponse?.accountWithViews.skip_traces?.used ?? 0,
    }),
    [creditsResponse]
  );

  return (
    <div className="atlas-pt-6">
      <Divider className="atlas-mb-6" />
      <p className="atlas-font-semibold atlas-text-xl atlas-mb-5 atlas-text-neutral-800">
        {t("dashboard.credit_summary.usage")}
      </p>
      <CurrentPlan />

      {hasFeature(Feature.masterMap) && (
        <UsageRow
          title={t("dashboard.credit_summary.os_mastermap_views")}
          used={mastermapTilesToViews(account?.mastermap_tiles?.used)}
          cap={mastermapTilesToViews(account?.mastermap_tiles?.cap)}
          resetDate={
            account?.mastermap_tiles.next_reset_date
              ? formatDate(account?.mastermap_tiles.next_reset_date)
              : ""
          }
          isUnlimited={hasFeature(Feature.unlimitedMasterMapClicks)}
        >
          {hasFeature(Feature.unlimitedMasterMapClicks) ? (
            t("dashboard.credit_summary.currently_running_test_to_provide_unlimited_map_views")
          ) : (
            <>
              {t("dashboard.credit_summary.used_when_viewing_os_mastermap")}
              <a
                href="https://support.landinsight.io/account-management/credits-and-views/how-many-os-map-views-do-i-have-left"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("dashboard.credit_summary.here")}
              </a>
              .
            </>
          )}
        </UsageRow>
      )}

      {hasFeature(Feature.usAccess) && hasBetaFeature(Feature.skipTracingBeta) && (
        <UsageRow
          title="Skip trace credits"
          progressBarColor="atlas-bg-blue-600"
          used={accountSkipTraceCredit.used}
          cap={accountSkipTraceCredit.cap}
          unlimitedMessage="Unlimited skip trace credits"
          isUnlimited={false}
          resetDate={formatDate(ownershipViewsResetDate)}
        >
          Used for performing a skip trace on a saved site. See your skip tracing results{" "}
          <a href="sites/skip-tracing">here</a>.
        </UsageRow>
      )}

      {hasFeature(Feature.ownershipLayer) && (
        <div>
          <UsageRow
            title={t("dashboard.credit_summary.ownership_clicks")}
            progressBarColor="atlas-bg-blue-600"
            used={account?.ownership_views.used}
            cap={account?.ownership_views.cap}
            unlimitedMessage={t("dashboard.credit_summary.unlimited")}
            isUnlimited={hasFeature(Feature.unlimitedOwnershipClicks)}
            resetDate={formatDate(ownershipViewsResetDate)}
          >
            {t("dashboard.credit_summary.used_if_you_click_on")}
            {ownershipViewsBillingCycleStart && (
              <OwnershipViews fromDate={ownershipViewsBillingCycleStart} />
            )}
          </UsageRow>
        </div>
      )}

      {hasFeature(Feature.planningLayer) && (
        <UsageRow
          title={t("dashboard.credit_summary.planning_views")}
          used={account?.planning_views.used}
          cap={account?.planning_views.cap}
          unlimitedMessage={t("dashboard.credit_summary.unlimited")}
          isUnlimited={hasFeature(Feature.unlimitedPlanningClicks)}
          resetDate={
            account?.planning_views.next_reset_date
              ? formatDate(account?.planning_views.next_reset_date)
              : ""
          }
        >
          {t("dashboard.credit_summary.used_when_you_view")}
        </UsageRow>
      )}
    </div>
  );
};
